import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import MediaGuidanceFields from '../../common/MediaGuidanceFields';

import type { MediaFolder } from 'js/src/client';
import { ApiService, MusicRecommendationModeEnum, WorkspaceMediaModeEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import type { RadioInputValue } from 'js/src/components/common/ui/RadioInput';
import RadioInput from 'js/src/components/common/ui/RadioInput';
import SwitchToggle from 'js/src/components/common/ui/SwitchToggle';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import { getAllArticleMedia } from 'js/src/data/article/article-selectors';
import {
    selectDamMediaEnabled,
    selectMediaGuidance,
    selectMediaSourcesWarningSeverity,
    selectMinimumStockSuitabilityScore,
    selectMusicRecommendationMode,
    selectStockMediaEnabled,
    selectWorkspaceFolderIds,
    selectWorkspaceMediaMode,
    updateDamMediaEnabledOperation,
    updateMediaGuidanceOperation,
    updateMinimumStockSuitabilityScoreOperation,
    updateMusicRecommendationModeOperation,
    updateStockMediaEnabledOperation,
    updateWorkspaceFolderIdsOperation,
    updateWorkspaceMediaModeOperation,
} from 'js/src/data/media-recommendation-preferences';
import Flags from 'js/src/libs/flags';
import { formatMediaGuidance, parseMediaGuidance } from 'js/src/libs/mediaGuidanceUtils';
import { useAppDispatch } from 'js/src/libs/RootState';

type MediaSourcesSettingsProps = {
    className?: string;
};

const HIGH_QUALITY_MIN_SUITABILITY_SCORE = 55;

export const MediaSourcesSettings: React.FC<MediaSourcesSettingsProps> = ({ className = '' }) => {
    const dispatch = useAppDispatch();

    const stockMediaEnabled = useSelector(selectStockMediaEnabled);
    const damMediaEnabled = useSelector(selectDamMediaEnabled);
    const workspaceMediaMode = useSelector(selectWorkspaceMediaMode);
    const mediaGuidance = useSelector(selectMediaGuidance);
    const selectedFolderIds = useSelector(selectWorkspaceFolderIds);
    const warningSeverity = useSelector(selectMediaSourcesWarningSeverity);
    const minimumStockSuitabilityScore = useSelector(selectMinimumStockSuitabilityScore);
    const musicRecommendationMode = useSelector(selectMusicRecommendationMode);
    const articleMedia = useSelector(getAllArticleMedia);
    const sourceMediaCount = Object.keys(articleMedia).length;

    // Derive workspace media enabled from mode (OFF = disabled, anything else = enabled)
    const workspaceMediaEnabled = workspaceMediaMode !== WorkspaceMediaModeEnum.OFF;

    const canSeeDam = Flags.canSeeDamTab();

    const parsedGuidance = parseMediaGuidance(mediaGuidance);

    const [allFolders, setAllFolders] = useState<MediaFolder[]>([]);
    const [loadingFolders, setLoadingFolders] = useState(false);
    const [starredMusicCount, setStarredMusicCount] = useState<number | null>(null);
    const [loadingStarredMusicCount, setLoadingStarredMusicCount] = useState(false);

    // Load folders list when workspace media is enabled and mode is folders
    useEffect(() => {
        if (workspaceMediaEnabled && workspaceMediaMode === WorkspaceMediaModeEnum.FOLDERS) {
            const loadFolders = async () => {
                setLoadingFolders(true);
                try {
                    const response = await ApiService.mediaFoldersList();
                    setAllFolders(response.data);
                } catch (error) {
                    Toast.error('Failed to load folders');
                }
                setLoadingFolders(false);
            };
            void loadFolders();
        }
    }, [workspaceMediaEnabled, workspaceMediaMode]);

    // Load starred music count when the component mounts
    useEffect(() => {
        const loadStarredMusicCount = async () => {
            setLoadingStarredMusicCount(true);
            try {
                const response = await ApiService.favoritesListMarkedFavoritesList({
                    query: {
                        search_audio: true,
                    },
                });
                const count = response.data.length;
                setStarredMusicCount(count);
            } catch (error) {
                Toast.error('Failed to load starred music count');
            }
            setLoadingStarredMusicCount(false);
        };
        void loadStarredMusicCount();
    }, []);

    const handleStockMediaToggle = useCallback(() => {
        dispatch(updateStockMediaEnabledOperation(!stockMediaEnabled));
    }, [dispatch, stockMediaEnabled]);

    const handleDamMediaToggle = useCallback(() => {
        dispatch(updateDamMediaEnabledOperation(!damMediaEnabled));
    }, [dispatch, damMediaEnabled]);

    const handleWorkspaceMediaToggle = useCallback(() => {
        if (workspaceMediaEnabled) {
            dispatch(updateWorkspaceMediaModeOperation(WorkspaceMediaModeEnum.OFF));
        } else {
            dispatch(updateWorkspaceMediaModeOperation(WorkspaceMediaModeEnum.ALL));
        }
    }, [dispatch, workspaceMediaEnabled]);

    const handleWorkspaceMediaModeChange = useCallback(
        (value: RadioInputValue) => {
            dispatch(updateWorkspaceMediaModeOperation(value as WorkspaceMediaModeEnum));
        },
        [dispatch],
    );

    const handleMediaGuidanceChange = useCallback(
        (field: 'region' | 'ethnicity' | 'general', value: string) => {
            const newParsed = { ...parsedGuidance, [field]: value };
            const formattedGuidance = formatMediaGuidance(
                newParsed.region,
                newParsed.ethnicity,
                newParsed.general,
            );
            dispatch(updateMediaGuidanceOperation(formattedGuidance));
        },
        [dispatch, parsedGuidance],
    );

    const onRegionChange = useCallback(
        (e: TypeInputChangeEvent) => {
            handleMediaGuidanceChange('region', e.target.value);
        },
        [handleMediaGuidanceChange],
    );

    const onEthnicityChange = useCallback(
        (e: TypeInputChangeEvent) => {
            handleMediaGuidanceChange('ethnicity', e.target.value);
        },
        [handleMediaGuidanceChange],
    );

    const onGeneralChange = useCallback(
        (e: TypeInputChangeEvent) => {
            handleMediaGuidanceChange('general', e.target.value);
        },
        [handleMediaGuidanceChange],
    );

    const handleFolderToggle = useCallback(
        (folderId: number) => {
            const newIds = selectedFolderIds.includes(folderId)
                ? selectedFolderIds.filter((id) => id !== folderId)
                : [...selectedFolderIds, folderId];
            dispatch(updateWorkspaceFolderIdsOperation(newIds));
        },
        [dispatch, selectedFolderIds],
    );

    const handleHighQualityMediaToggle = useCallback(() => {
        const newScore =
            minimumStockSuitabilityScore === null ? HIGH_QUALITY_MIN_SUITABILITY_SCORE : null;
        dispatch(updateMinimumStockSuitabilityScoreOperation(newScore));
    }, [dispatch, minimumStockSuitabilityScore]);

    const handleMusicRecommendationModeChange = useCallback(
        (value: RadioInputValue) => {
            dispatch(updateMusicRecommendationModeOperation(value as MusicRecommendationModeEnum));
        },
        [dispatch],
    );

    return (
        <div className={`media-sources-settings ${className}`}>
            <h6 className="font-l5 font-l5--regular fw-bold">Media Sources</h6>
            <p className="font-l5 font-l5--small color-grey-600 mb-l5-20">
                What media sources we should be using to find media for your videos?
            </p>

            {warningSeverity === 'error' && (
                <Callout
                    className="mb-l5-20"
                    msgType={CALLOUT_TYPES.ERROR}
                    title="No media sources enabled."
                    text="Enable at least one media source or select folders to generate videos with media."
                />
            )}
            {warningSeverity === 'info' && (
                <Callout
                    className="mb-l5-20"
                    msgType={CALLOUT_TYPES.ALERT}
                    title={`Only using ${sourceMediaCount} source media ${sourceMediaCount === 1 ? 'asset' : 'assets'}.`}
                    text="No other media sources are enabled which may minimize the media you see in your video."
                />
            )}

            {/* Stock Media */}
            <div className="mb-l5-16">
                <div className="d-flex align-items-center mb-l5-8">
                    <SwitchToggle checked={stockMediaEnabled} onChange={handleStockMediaToggle} />
                    <div className="ms-l5-12">
                        <h6>Stock Media</h6>
                        <div className="font-l5 font-l5--subtitle-sm color-grey-600">
                            Use high-quality stock photos and videos from our library
                        </div>
                    </div>
                </div>
            </div>

            {/* Workspace Media */}
            <div className="mb-l5-16">
                <div className="d-flex align-items-center mb-l5-8">
                    <SwitchToggle
                        checked={workspaceMediaEnabled}
                        onChange={handleWorkspaceMediaToggle}
                    />
                    <div className="ms-l5-12">
                        <h6>Workspace Media</h6>
                        <div className="font-l5 font-l5--subtitle-sm color-grey-600">
                            Use media previously uploaded to your workspace
                        </div>
                    </div>
                </div>
            </div>

            {workspaceMediaEnabled && (
                <div className="ms-l5-64 mb-l5-16">
                    <RadioInput
                        name="workspace-mode"
                        value="all"
                        text="All"
                        description="Pick from all previously uploaded media"
                        checked={workspaceMediaMode === WorkspaceMediaModeEnum.ALL}
                        onChange={handleWorkspaceMediaModeChange}
                        className="mb-l5-16"
                    />
                    <RadioInput
                        name="workspace-mode"
                        value="folders"
                        text="Folders"
                        description="Pick media from specific folders"
                        checked={workspaceMediaMode === WorkspaceMediaModeEnum.FOLDERS}
                        onChange={handleWorkspaceMediaModeChange}
                        className="mb-l5-16"
                    />

                    {/* Folders List */}
                    {workspaceMediaMode === WorkspaceMediaModeEnum.FOLDERS && (
                        <div className="ms-l5-32 mb-l5-16">
                            {loadingFolders ? (
                                <div className="text-center py-l5-16">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading folders...</span>
                                    </div>
                                </div>
                            ) : allFolders.length > 0 ? (
                                <div>
                                    {allFolders.map((folder) => {
                                        const isSelected = selectedFolderIds.includes(folder.id);
                                        return (
                                            <div
                                                key={folder.id}
                                                className="d-flex align-items-center mb-l5-8"
                                            >
                                                <SwitchToggle
                                                    checked={isSelected}
                                                    onChange={() => handleFolderToggle(folder.id)}
                                                />
                                                <span className="font-l5 font-l5--small ms-l5-12">
                                                    {folder.name}
                                                    <span className="ms-l5-8 color-grey-600">
                                                        ({folder.media_count}{' '}
                                                        {folder.media_count === 1
                                                            ? 'asset'
                                                            : 'assets'}
                                                        )
                                                    </span>
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="font-l5 font-l5--small color-grey-600">
                                    No folders found. Create folders in your media library to
                                    organize your uploads.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* DAM Media */}
            <div className="mb-l5-16">
                <div className="d-flex align-items-center mb-l5-8">
                    <SwitchToggle
                        checked={damMediaEnabled}
                        onChange={handleDamMediaToggle}
                        disabled={!canSeeDam}
                    />
                    <div className="ms-l5-12">
                        <h6>Brand Media (DAM)</h6>
                        <div className="font-l5 font-l5--subtitle-sm color-grey-600">
                            {canSeeDam ? (
                                'Use media from your Digital Asset Management system'
                            ) : (
                                <>
                                    No&nbsp;
                                    <a
                                        href="https://help.lumen5.com/en/article/dam-integration-1pc53d2/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        digital asset managers
                                    </a>
                                    &nbsp;setup on your account
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Media Guidance */}
            <div className="mt-l5-24 pt-l5-24 border-top">
                <h6 className="font-l5 font-l5--regular fw-bold">Media Guidance</h6>
                <p className="font-l5 font-l5--small color-grey-600 mb-l5-16">
                    Including media guidance will help the AI know what media to pick.
                </p>
                <MediaGuidanceFields
                    region={parsedGuidance.region}
                    ethnicity={parsedGuidance.ethnicity}
                    general={parsedGuidance.general}
                    onRegionChange={onRegionChange}
                    onEthnicityChange={onEthnicityChange}
                    onGeneralChange={onGeneralChange}
                />
            </div>

            {/* Music Mode */}
            <div className="mt-l5-24 pt-l5-24 border-top">
                <h6 className="font-l5 font-l5--regular fw-bold mb-l5-16">Music Mode</h6>
                <RadioInput
                    name="music-recommendation-mode"
                    value={MusicRecommendationModeEnum.RECOMMENDED}
                    text="Pick recommended music"
                    description="Let AI pick music that matches your script"
                    checked={musicRecommendationMode === MusicRecommendationModeEnum.RECOMMENDED}
                    onChange={handleMusicRecommendationModeChange}
                    className="mb-l5-16"
                />
                <RadioInput
                    name="music-recommendation-mode"
                    value={MusicRecommendationModeEnum.STARRED}
                    text={
                        loadingStarredMusicCount
                            ? 'Pick from starred music (Loading...)'
                            : starredMusicCount === 0
                              ? 'Pick from starred music (No starred music found)'
                              : starredMusicCount === null
                                ? 'Pick from starred music'
                                : starredMusicCount >= 1000
                                  ? 'Pick from starred music (1000+ tracks)'
                                  : `Pick from starred music (${starredMusicCount} tracks)`
                    }
                    description="Pick music from your starred collection"
                    checked={musicRecommendationMode === MusicRecommendationModeEnum.STARRED}
                    onChange={handleMusicRecommendationModeChange}
                    disabled={loadingStarredMusicCount || starredMusicCount === 0}
                    className="mb-l5-16"
                />
                <RadioInput
                    name="music-recommendation-mode"
                    value={MusicRecommendationModeEnum.OFF}
                    text="Off"
                    description="Don't add music to videos"
                    checked={musicRecommendationMode === MusicRecommendationModeEnum.OFF}
                    onChange={handleMusicRecommendationModeChange}
                    className="mb-l5-16"
                />
            </div>

            {/* Recommendation Mode */}
            {stockMediaEnabled && (
                <div className="mt-l5-24 pt-l5-24 border-top">
                    <h6 className="font-l5 font-l5--regular fw-bold">Media Recommendation Mode</h6>
                    <div className="mb-l5-16">
                        <div className="d-flex align-items-center mb-l5-8">
                            <SwitchToggle
                                checked={minimumStockSuitabilityScore !== null}
                                onChange={handleHighQualityMediaToggle}
                            />
                            <div className="ms-l5-12">
                                <h6>Prefer higher quality media</h6>
                                <div className="font-l5 font-l5--subtitle-sm color-grey-600">
                                    Pick media that is visually more appealing even if it is less
                                    specific or relevant
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
